/**
 * sendFailureRetry.ts — Round J shared constants (2026-07-14).
 *
 * A dispatched send that dies without any visible outcome (dead handler
 * path, empty VM terminal, watchdog timeout) is surfaced as a retryable
 * error bubble. The bubble reuses the existing interrupted/regeneratePrompt
 * affordance (GlobalChatSystem renders the one-click button); this sourceTag
 * is how the renderer knows to label the button "Retry" and to resend
 * WITHOUT re-appending the user bubble (it already exists in the thread).
 *
 * Leaf module on purpose — imported by both the chat components and the
 * globalTools hooks (onDone empty-turn guard) without dragging either
 * layer's dependency graph into the other.
 */

import type { ChatMessage } from './types';

export const SEND_FAILURE_RETRY_SOURCE_TAG = 'send_failure_retry';

export const SEND_FAILURE_RETRY_TEXT =
  "Prest0n couldn't start this reply. Tap retry to send it again.";

/**
 * Empty-terminal copy (2026-09-02, the butterfly-thread report). The turn DID
 * start — the clock ran, the server answered `done` — but delivered no text
 * and painted no surface. Labeling that "couldn't start" sent the owner
 * looking for a send failure that never happened; the failure class is the
 * reply coming back empty, and the copy says so. Same Retry affordance.
 */
export const SEND_FAILURE_EMPTY_REPLY_TEXT =
  "Prest0n's reply came back empty. Tap retry to send it again.";

/**
 * Finding 8d (2026-07-15): a failed ATTACHMENT-ONLY turn has no original
 * text, so `regeneratePrompt` is empty and the renderer draws NO Retry
 * button. The copy must not promise a "Tap retry" affordance that doesn't
 * exist — direct the user to re-send the attachment instead.
 */
export const SEND_FAILURE_ATTACHMENT_ONLY_TEXT =
  "Prest0n couldn't process this. Please re-send the attachment.";

/**
 * Bubble copy for a failed send: retryable copy when the original text is
 * non-empty (a Retry button will render from regeneratePrompt), attachment-
 * only copy when it is empty (no button renders — never advertise one).
 */
export function sendFailureBubbleText(originalText: string | null | undefined): string {
  return (originalText || '').trim()
    ? SEND_FAILURE_RETRY_TEXT
    : SEND_FAILURE_ATTACHMENT_ONLY_TEXT;
}

/**
 * Round J.1 (adversarial-review Finding 1, 2026-07-15): live equivalent of
 * `clearStaleInterruptedCards` for watchdog retry bubbles. When a LATE ack
 * proves the "dead" turn is actually alive, the thread's YOUNGEST
 * send_failure_retry bubble is stale — its Retry would double-dispatch the
 * turn. Remove exactly that one bubble (older failure bubbles belong to
 * genuinely-failed earlier turns and keep their affordance).
 *
 * Pure; returns the ORIGINAL array reference when nothing changed.
 */
export function stripYoungestSendFailureBubble(
  messages: ChatMessage[],
): { messages: ChatMessage[]; changed: boolean } {
  if (!Array.isArray(messages) || messages.length === 0) {
    return { messages, changed: false };
  }
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m && !m.isUser && m.sourceTag === SEND_FAILURE_RETRY_SOURCE_TAG) {
      return {
        messages: messages.filter((_, idx) => idx !== i),
        changed: true,
      };
    }
  }
  return { messages, changed: false };
}
